import type { IndustryPage } from "@/lib/data";

export const tradesmen: IndustryPage = {
  slug: "tradesmen",
  seoTitle:
    "Web Design for Tradesmen UK | Fixed £1,000, Live in 7 Days",
  seoDescription:
    "Web design for tradesmen across the UK: a page per job type, your service areas, reviews and a quote form. Fixed £1,000, live in 7 days. Book a free call.",
  tldr:
    "ConstructionWebDesign.uk offers web design for tradesmen across the UK: a mobile-first website with a page for every job you take, the areas you cover, your reviews and a quote form. The price is £1,000, one-off and fixed, with no subscription, or £1,399 with logo and branding. It goes live in 7 days and you own the website.",
  serviceName: "Website design for tradesmen",
  pricingFromGBP: 1000,
  guides: [
    { href: "/blog/how-much-should-a-construction-website-cost", label: "How much does a construction website cost in the UK?" },
    { href: "/blog/how-to-get-more-construction-leads", label: "How to get more construction leads without paying" },
    { href: "/blog/local-seo-for-builders", label: "Local SEO for builders: rank for \"builder near me\"" },
    { href: "/blog/google-business-profile-for-builders", label: "Google Business Profile for builders: setup guide" },
  ],
  hero: {
    eyebrow: "Trades website design · UK",
    headline: "Website design for tradesmen, built around the jobs you take.",
    highlight: "built around the jobs you take",
    sub: "Websites for UK tradespeople with a page for every job you take, pages for the towns you cover, your insurance and qualifications, your reviews, one-tap calling and a quote form.",
    secondaryCta: { label: "See tradesman demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Electrican.jpg",
      alt: "Tradesman in a tool belt pulling cable through a stud-framed room during first fix",
      headlineTop: "Keep the diary full",
      headlineBottom: "all year round.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/solidcraft.jpg",
    },
  ],
  demoSlugs: ["solidcraft", "brassline", "voltline", "joinwright", "ridgemark"],
  demosHeading: "Tradesman website demo templates.",
  demosSub:
    "Demo templates from across the trades: brickwork, plumbing, electrical, joinery and roofing. Open any of them to see a layout ConstructionWebDesign.uk can adapt to the jobs you take.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why do tradespeople need a website?",
    intro:
      "Tradespeople need a website because customers often look them up before they ring, even after a recommendation. A website puts your jobs, the areas you cover, your credentials and your reviews in one place that you own.",
    points: [
      {
        title: "Turn a recommendation into a booking.",
        body: "A customer who hears your name from a neighbour often searches for it before calling. If that search turns up only a Facebook page, the customer has little to go on. A page for each job type gives them the detail they were looking for.",
        icon: "Spanner",
      },
      {
        title: "Bring in the jobs you actually want.",
        body: "List the work you want more of and leave off what you don't. Naming the jobs, the typical job sizes and how you price them puts off enquiries that were never going to suit you.",
        icon: "Check",
      },
      {
        title: "Target the towns you actually drive to.",
        body: "On-page local SEO and a page for each town you cover tell Google where you work. The aim is to be found for searches like \"plasterer near me\" in the villages and suburbs you actually drive to, not across the whole county. Linking the website to your Google Business Profile helps too.",
        icon: "Pin",
      },
      {
        title: "Show you're insured and qualified.",
        body: "Customers want to know you're covered before they let you through the door. Public liability cover, City & Guilds or NVQ qualifications, a CSCS card, and TrustMark or trade-body membership go near the top of the page, if you hold them, with numbers a customer can check.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does web design for tradesmen include?",
    intro:
      "Tradesmen website design from ConstructionWebDesign.uk includes a page for every job you take, pages for the towns you cover, your credentials and reviews, one-tap calling and a quote form. Each part is there to bring in calls or to help you decide which jobs to take.",
    items: [
      {
        title: "A page for every job you take.",
        body: "Each job type gets its own page with photos, a typical price band and how long the work takes, rather than one merged list of services. Separate pages give each job its own chance to be found on Google and answer the customer's first questions before they ring.",
      },
      {
        title: "One-tap call and a sticky number bar.",
        body: "Customers often search for a tradesperson on a phone. Your number sits at the top of the first screen and stays visible as they scroll, so they can ring you without hunting for a contact page.",
      },
      {
        title: "Your own job photos, not a stock library.",
        body: "A project gallery shows finished work from the postcodes you cover. Photos of your own jobs tell a customer far more about the standard of your work than stock photography can.",
      },
      {
        title: "Area pages for the towns you cover.",
        body: "One page for each town or postcode area you genuinely work in, written for that place rather than copied from a template. Google's spam policies treat near-identical town pages as doorway pages, so each area page describes the work you actually do there.",
      },
      {
        title: "Insurance, qualification and TrustMark badges in the header.",
        body: "If you hold public liability cover, City & Guilds, an NVQ, a CSCS card, TrustMark registration or trade-body membership, it goes where the eye starts, with registration numbers linked to the official register where one exists. Credentials you don't hold are left off.",
      },
      {
        title: "Real reviews from Google, Checkatrade, MyBuilder and Rated People.",
        body: "Genuine reviews you already have are shown on the website with a link back to each profile, so a customer can check them at the source. Only real reviews are used.",
      },
      {
        title: "A quote form that does the sorting.",
        body: "The quote form asks for the postcode, job type, photos and rough timing, so each enquiry arrives with enough detail to price the job, or turn it down, without a site visit.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a tradesman website cost in the UK?",
      a: "A tradesman website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. Typical UK ranges elsewhere are about £4,000–£5,000 from a freelancer (billed £50–£120 an hour), £5,000–£25,000 from an agency, and about £22 a month for Wix or Squarespace, which adds up over the years.",
    },
    {
      q: "How long does it take to build a tradesman website?",
      a: "A tradesman website from ConstructionWebDesign.uk goes live in 7 days from the discovery call. On that free 30-minute call, you explain the jobs you take, where you work and which work you want more of. The steps after it are strategy and sitemap, design, build, then launch and support. A typical UK agency takes 8–12 weeks.",
    },
    {
      q: "What should a tradesman website include?",
      a: "A tradesman website should include a page for each job you take, a page for each town you cover, your insurance and qualifications, photos of finished jobs, genuine reviews, a one-tap phone number and a short quote form. Trades website design from ConstructionWebDesign.uk covers all of those parts, with badges shown only for credentials you actually hold, such as TrustMark registration or a CSCS card.",
    },
    {
      q: "Do tradesmen need a website if they get work by word of mouth?",
      a: "Yes, tradesmen still need a website when most work comes by word of mouth, because a recommended name is often searched before anyone rings. A website shows that customer your jobs, the areas you cover, your credentials and your reviews in one place. Without one, a search for your name may turn up nothing that helps the customer choose you.",
    },
    {
      q: "Is a website better than Checkatrade, MyBuilder or Rated People?",
      a: "Websites for tradespeople and directories such as Checkatrade, MyBuilder or Rated People do different jobs, so neither is simply better and many trades use both. Directories charge membership or lead fees, and customers see other trades on the same page. A website from ConstructionWebDesign.uk sends enquiries straight to you, keeps working if you stop paying a directory, and you own it outright. The website can also link to your directory reviews.",
    },
    {
      q: "Can one website cover more than one trade?",
      a: "Yes, one tradesman website can cover every trade you offer, with each trade given its own page. A customer searching for a single job, such as tiling or fencing, then lands on a page about that job rather than a long merged list. ConstructionWebDesign.uk plans those pages in the sitemap, which you sign off before design starts.",
    },
    {
      q: "How can a tradesman get found on Google?",
      a: "A tradesman gets found on Google through a website with a page for each job and each town covered, a complete Google Business Profile and genuine reviews. Good web design for trades builds the first part in: every website from ConstructionWebDesign.uk includes on-page local SEO, so page titles, headings and copy match what local customers search. No one can guarantee a ranking, but these are the foundations.",
    },
  ],
  finalCta: {
    headline: "Get a website built around your trade.",
    highlight: "your trade",
    sub: "Book a free 30-minute call to talk through the jobs you take and the areas you cover. The website costs a fixed £1,000 and goes live in 7 days.",
  },
};
